import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  CircularProgress,
  Tooltip,
  Alert
} from '@mui/material';
import {
  CheckCircle as HealthyIcon,
  Error as ErrorIcon,
  Storage as DatabaseIcon,
  Hub as MCPIcon,
  Psychology as AIIcon,
  Cloud as LLMIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { procogiaBrand } from '../theme/procogia';
import { apiService } from '../services/apiService';

interface SystemService {
  name: string;
  status: 'healthy' | 'warning' | 'error';
  message: string;
  critical: boolean;
  responseTime?: number;
  timestamp: string;
}

interface HealthData {
  services: SystemService[];
  summary: {
    total: number;
    passed: number;
    failed: number;
    healthPercentage: number;
  };
  overall: 'healthy' | 'warning' | 'critical';
  timestamp: string;
}

interface SystemStatusProps {
  refreshInterval?: number; // in milliseconds, default 30 seconds
}

const SystemStatus: React.FC<SystemStatusProps> = ({ refreshInterval = 30000 }) => {
  const [healthData, setHealthData] = useState<HealthData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  // Map backend service names to our display categories
  const getSystemStatus = (services: SystemService[]) => {
    const database = services.find(s => s.name === 'database-connection');
    const mcpServers = services.filter(s => s.name.startsWith('mcp-'));
    const aiAgent = services.find(s => s.name === 'ai-agent');
    const llmConnection = services.find(s => s.name === 'openai-integration');

    return {
      database: {
        status: database?.status || 'error',
        message: database?.message || 'Database status unknown',
        responseTime: database?.responseTime
      },
      mcpServers: {
        status: mcpServers.every(s => s.status === 'healthy') ? 'healthy' : 
               mcpServers.some(s => s.status === 'error') ? 'error' : 'warning',
        message: `${mcpServers.filter(s => s.status === 'healthy').length}/${mcpServers.length} MCP servers healthy`,
        count: mcpServers.length,
        healthy: mcpServers.filter(s => s.status === 'healthy').length
      },
      aiAgent: {
        status: aiAgent?.status || 'error',
        message: aiAgent?.message || 'AI Agent status unknown',
        responseTime: aiAgent?.responseTime
      },
      llmConnection: {
        status: llmConnection?.status || 'error',
        message: llmConnection?.message || 'LLM Connection status unknown',
        responseTime: llmConnection?.responseTime
      }
    };
  };

  const fetchHealthData = async () => {
    try {
      setError(null);
      const response = await apiService.get('/health/comprehensive');
      if (response.success) {
        setHealthData(response.data);
        setLastUpdated(new Date());
      } else {
        throw new Error('Health check failed');
      }
    } catch (err: any) {
      console.error('Failed to fetch health data:', err);
      setError(err.message || 'Failed to fetch system status');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHealthData();
    
    // Set up polling
    const interval = setInterval(fetchHealthData, refreshInterval);
    
    return () => clearInterval(interval);
  }, [refreshInterval]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'healthy':
        return procogiaBrand.auth0.success;
      case 'warning':
        return procogiaBrand.status.warning;
      case 'error':
      default:
        return procogiaBrand.auth0.error;
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'healthy':
        return <HealthyIcon sx={{ color: procogiaBrand.auth0.success }} />;
      case 'warning':
        return <ErrorIcon sx={{ color: procogiaBrand.status.warning }} />;
      case 'error':
      default:
        return <ErrorIcon sx={{ color: procogiaBrand.auth0.error }} />;
    }
  };

  const StatusIndicator: React.FC<{
    title: string;
    icon: React.ReactNode;
    status: string;
    message: string;
    responseTime?: number;
    additionalInfo?: string;
  }> = ({ title, icon, status, message, responseTime, additionalInfo }) => (
    <Card 
      elevation={2}
      sx={{ 
        height: '100%',
        border: `2px solid ${getStatusColor(status)}`,
        backgroundColor: status === 'healthy' ? 'rgba(149, 193, 0, 0.05)' : 
                         status === 'warning' ? 'rgba(31, 184, 204, 0.05)' : 
                         'rgba(244, 67, 54, 0.05)',
        transition: 'all 0.3s ease'
      }}
    >
      <CardContent>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
          <Box display="flex" alignItems="center" gap={1}>
            {icon}
            <Typography variant="h6" fontWeight="600">
              {title}
            </Typography>
          </Box>
          {getStatusIcon(status)}
        </Box>
        
        <Tooltip title={message} arrow>
          <Chip
            label={status.toUpperCase()}
            size="small"
            sx={{
              backgroundColor: getStatusColor(status),
              color: 'white',
              fontWeight: 'bold',
              mb: 1
            }}
          />
        </Tooltip>
        
        <Typography variant="body2" color="text.secondary" noWrap>
          {message}
        </Typography>
        
        {additionalInfo && (
          <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
            {additionalInfo}
          </Typography>
        )}
        
        {responseTime !== undefined && (
          <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
            Response: {responseTime}ms
          </Typography>
        )}
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <Card elevation={3}>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="center" py={4}>
            <CircularProgress size={40} sx={{ color: procogiaBrand.blue.main }} />
            <Typography variant="h6" ml={2}>
              Loading System Status...
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card elevation={3}>
        <CardContent>
          <Alert 
            severity="error" 
            action={
              <RefreshIcon 
                sx={{ cursor: 'pointer' }} 
                onClick={fetchHealthData}
              />
            }
          >
            <Typography variant="body1">
              Failed to load system status: {error}
            </Typography>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  if (!healthData) {
    return (
      <Card elevation={3}>
        <CardContent>
          <Alert severity="warning">
            <Typography variant="body1">
              No system status data available
            </Typography>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  const systemStatus = getSystemStatus(healthData.services);

  return (
    <Card elevation={3}>
      <CardContent>
        <Box display="flex" alignItems="center" justifyContent="between" mb={3}>
          <Box display="flex" alignItems="center" gap={2}>
            <Typography variant="h5" fontWeight="bold" color={procogiaBrand.auth0.header}>
              🖥️ System Status
            </Typography>
            <Chip
              label={`${healthData.summary.healthPercentage}% Healthy`}
              size="small"
              sx={{
                backgroundColor: healthData.overall === 'healthy' ? procogiaBrand.auth0.success : 
                                healthData.overall === 'warning' ? procogiaBrand.status.warning : 
                                procogiaBrand.auth0.error,
                color: 'white',
                fontWeight: 'bold'
              }}
            />
          </Box>
          
          <Box textAlign="right">
            <Typography variant="caption" color="text.secondary" display="block">
              Last Updated
            </Typography>
            <Typography variant="body2" color="text.primary">
              {lastUpdated ? lastUpdated.toLocaleTimeString() : 'Never'}
            </Typography>
          </Box>
        </Box>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <StatusIndicator
              title="Database"
              icon={<DatabaseIcon sx={{ color: procogiaBrand.blue.main }} />}
              status={systemStatus.database.status}
              message={systemStatus.database.message}
              responseTime={systemStatus.database.responseTime}
            />
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <StatusIndicator
              title="MCP Servers"
              icon={<MCPIcon sx={{ color: procogiaBrand.blue.main }} />}
              status={systemStatus.mcpServers.status}
              message={systemStatus.mcpServers.message}
              additionalInfo={`${systemStatus.mcpServers.healthy}/${systemStatus.mcpServers.count} servers online`}
            />
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <StatusIndicator
              title="AI Agent"
              icon={<AIIcon sx={{ color: procogiaBrand.blue.main }} />}
              status={systemStatus.aiAgent.status}
              message={systemStatus.aiAgent.message}
              responseTime={systemStatus.aiAgent.responseTime}
            />
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <StatusIndicator
              title="LLM Status"
              icon={<LLMIcon sx={{ color: procogiaBrand.blue.main }} />}
              status={systemStatus.llmConnection.status}
              message={systemStatus.llmConnection.message}
              responseTime={systemStatus.llmConnection.responseTime}
            />
          </Grid>
        </Grid>

        <Box mt={3} p={2} sx={{ backgroundColor: procogiaBrand.gray.lightest, borderRadius: 1 }}>
          <Typography variant="body2" color="text.secondary" textAlign="center">
            🔄 Auto-refreshes every {refreshInterval / 1000} seconds • 
            Total Services: {healthData.summary.total} • 
            Healthy: {healthData.summary.passed} • 
            Failed: {healthData.summary.failed}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
};

export default SystemStatus; 